/**
 * VariableMappingsEditor — Story 11.4b.
 *
 * Editor para `variable_mappings: Record<string, VariableKey>` onde:
 *   - keys são posições string `"1"..."99"` (cada uma corresponde a `{{N}}` no
 *     template Meta);
 *   - values são `VariableKey` do `VARIABLE_REGISTRY` (defense in depth: o
 *     backend valida o mesmo shape via TypeBox + CHECK constraint).
 *
 * Diferente do `templates/VariablesEditor.tsx` (que edita array
 * `[{name, position, example?}]` do schema `meta_templates.variables`), aqui
 * editamos um Record posicional — Op escolhe POR VARIANTE qual field do
 * contexto vai pra cada slot. Reusar parcialmente o componente de templates
 * complicaria mais do que ajudaria: shapes, semântica e affordances diferem.
 *
 * Add slot: auto-incrementa pra próxima posição não usada.
 * Remove slot: NÃO renumera (mantém contrato `{{N}}` ↔ slot estável; gaps OK).
 */

import { useMemo } from 'react';
import { Plus, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  VARIABLE_REGISTRY,
  VARIABLE_SAMPLES,
  type VariableKey,
} from '@/constants/variable-registry';

export type VariableMappings = Record<string, VariableKey>;

interface Props {
  value: VariableMappings;
  onChange: (next: VariableMappings) => void;
  disabled?: boolean;
  id?: string;
}

function isVariableKey(v: string): v is VariableKey {
  return (VARIABLE_REGISTRY as readonly string[]).includes(v);
}

function nextSequentialPosition(value: VariableMappings): number {
  const used = Object.keys(value)
    .map((k) => Number(k))
    .filter((n) => Number.isInteger(n) && n >= 1);
  if (used.length === 0) return 1;
  return Math.max(...used) + 1;
}

function sortPositions(value: VariableMappings): string[] {
  return Object.keys(value).sort((a, b) => Number(a) - Number(b));
}

export function VariableMappingsEditor({ value, onChange, disabled, id }: Props) {
  const positions = useMemo(() => sortPositions(value), [value]);

  function handleAddRow() {
    const position = String(nextSequentialPosition(value));
    onChange({ ...value, [position]: 'customer_name' });
  }

  function handleRemoveRow(position: string) {
    const next = { ...value };
    delete next[position];
    onChange(next);
  }

  function handleFieldChange(position: string, fieldKey: string) {
    if (!isVariableKey(fieldKey)) return;
    onChange({ ...value, [position]: fieldKey });
  }

  function handlePositionChange(oldPosition: string, newPositionRaw: string) {
    const newPosition = newPositionRaw.trim();
    if (newPosition === oldPosition || !/^[1-9]\d?$/.test(newPosition)) return;
    if (Object.prototype.hasOwnProperty.call(value, newPosition)) return; // colisão silenciosa
    const next: VariableMappings = {};
    for (const [k, v] of Object.entries(value)) {
      if (k === oldPosition) next[newPosition] = v;
      else next[k] = v;
    }
    onChange(next);
  }

  return (
    <div className="space-y-2" id={id}>
      <Label>Mapeamento de variáveis</Label>
      <p className="text-xs text-muted-foreground">
        Cada slot <code>{'{{N}}'}</code> do template é resolvido contra um field do contexto da
        conversa. Slots sem mapeamento usam o schema padrão do template Meta (fallback).
      </p>

      {positions.length === 0 ? (
        <div className="rounded-md border border-dashed bg-muted/30 px-3 py-4 text-center text-xs text-muted-foreground">
          Nenhum mapping configurado — variante usa o schema do template Meta.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-md border">
          <table
            className="w-full text-sm"
            role="table"
            aria-label="Mapeamento de variáveis da variante"
          >
            <thead className="border-b bg-muted/40 text-left text-xs uppercase text-muted-foreground">
              <tr>
                <th scope="col" className="px-3 py-2 font-medium">
                  Posição
                </th>
                <th scope="col" className="px-3 py-2 font-medium">
                  Field do contexto
                </th>
                <th scope="col" className="px-3 py-2 font-medium">
                  Sample preview
                </th>
                <th scope="col" className="px-3 py-2 text-right font-medium">
                  <span className="sr-only">Ações</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {positions.map((position) => {
                const fieldKey = value[position]!;
                const sample = VARIABLE_SAMPLES[fieldKey];
                return (
                  <tr key={position} className="border-b last:border-b-0">
                    <td className="px-3 py-2 align-middle font-mono text-xs">
                      <Input
                        aria-label={`Posição do slot ${position}`}
                        value={position}
                        onChange={(e) => handlePositionChange(position, e.target.value)}
                        disabled={disabled}
                        className="h-8 w-16"
                      />
                    </td>
                    <td className="px-3 py-2 align-middle">
                      <select
                        aria-label={`Field para slot ${position}`}
                        value={fieldKey}
                        onChange={(e) => handleFieldChange(position, e.target.value)}
                        disabled={disabled}
                        className="h-8 w-full rounded border bg-background px-2 text-sm"
                      >
                        {VARIABLE_REGISTRY.map((key) => (
                          <option key={key} value={key}>
                            {key}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-3 py-2 align-middle text-xs text-muted-foreground">
                      {sample}
                    </td>
                    <td className="px-3 py-2 text-right align-middle">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        disabled={disabled}
                        onClick={() => handleRemoveRow(position)}
                        aria-label={`Remover mapping ${position}`}
                      >
                        <Trash2 className="h-4 w-4" aria-hidden />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex justify-end">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled}
          onClick={handleAddRow}
        >
          <Plus className="mr-1 h-3 w-3" aria-hidden />
          Adicionar slot
        </Button>
      </div>
    </div>
  );
}
